import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import { toast } from "sonner";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import * as Sentry from "@sentry/react";

// ── Sentry client-side initialization ───────────────────────────
// Only active when VITE_SENTRY_DSN is set in the environment.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
    // Session replay must never capture wallet, payout, or account details in clear text.
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })],
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
  });
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;
  if (!isUnauthorized) return;

  // Only hard-redirect on protected routes — public pages silently degrade
  // when a user is anonymous (UNAUTHED on a public page is expected, not a logout event)
  const isProtectedRoute = window.location.pathname.startsWith("/console");
  if (!isProtectedRoute) return;

  window.location.href = getLoginUrl();
};

const extractApiErrorMessage = (error: unknown): string => {
  if (error instanceof TRPCClientError) {
    // tRPC HTTP errors carry the human message from TRPCError; strip the
    // status-code prefix httpBatchLink appends ("[UNAUTHORIZED] ..." etc.)
    return error.message.replace(/^\[\w+\]\s*/, "") || "Something went wrong. Please try again.";
  }
  return "Something went wrong. Please try again.";
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
    // User-initiated actions must never fail silently. Queries render their
    // own fallbacks, but mutations have no UI otherwise.
    if (!(error instanceof TRPCClientError) || error.message !== UNAUTHED_ERR_MSG) {
      toast.error(extractApiErrorMessage(error));
    }
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
